namespace Solovis.Reports.Parameters.Composites {


    export class GroupingDeclarationControl extends Solovis.UI.BaseControl {
        private _grouping: CompositeGroupingDeclarationData;
        private _formBuilder: Solovis.UI.InputControls.FormBuilder;        

        private _propDefs: InvData.Models.IdAndCodeObjectIndex<InvData.PropertyDefinition>;
        private _resourceOptions: Array<any>;
        private _defaultResourceRefValue: string;

        constructor(options) {
            super(options);

            this._grouping = options.grouping;

            this._defaultResourceRefValue = "InvestmentHoldingOrEntity";
            this._resourceOptions = [
                { label: "Investment/Holding", value: this._defaultResourceRefValue, code: "investmentHoldingOrEntity" },
                { label: "Investment/Holding/Owner", value: "InvestmentHoldingOwnerOrEntity", code: "investmentHoldingOwnerOrEntity" },
                { label: "Holding or Entity", value: "HoldingOrEntity", code: "entityOrHolding", resourceTypeId: InvData.ResourceTypeIds.Entity },
                { label: "Owner", value: "Owner", code: "owner", resourceTypeId: InvData.ResourceTypeIds.Entity },
                { label: "True Owner", value: "TrueOwner", code: "trueOwner", resourceTypeId: InvData.ResourceTypeIds.Entity },
                { label: "Absolute Owner", value: "AbsoluteOwner", code: "absoluteOwner" }, 
                { label: "Manager", value: "Manager", code: "manager", resourceTypeId: InvData.ResourceTypeIds.Manager },
            ];
        }

        public async doInitAsync(): Promise<void> {
            let sitePortalServices = new Solovis.Portal.SitePortalServices();
            this._propDefs = await sitePortalServices.modelEnvironmentCachedData().getPropertyDefinitionsAsync();
        }

        private _getSelectedPropDefIfPropGrouping() {
            if (this._formBuilder) {
                let declarationType = this._formBuilder.getValueByName("declarationType");
                if (declarationType === "PropertyValue") {
                    let property = this._formBuilder.getValueByName("property");
                    if (property) {
                        let propDef = this._propDefs.tryGetById(property.id);
                        return propDef;
                    }
                }
            }

            return null;
        }

        private _updateResourceRefEditor(editor) {
            const propDefValue = this._formBuilder.getValueByName("property");
            const propLocationId = propDefValue ? propDefValue.defaultPropertyLocationId : null;

            // Define the mapping from `propDef` values to `resourceRef` codes
            const propDefToResourceRefMap = {
              0: "investmentHoldingOrEntity",         // NotSet
              1: "investmentHoldingOrEntity",         // Investment
              2: "investmentHoldingOrEntity",         // InvestmentHoldingOrEntity
              3: "investmentHoldingOwnerOrEntity",    // InvestmentHoldingOwnerOrEntity
              4: "entityOrHolding",                   // HoldingOrEntity
              5: "owner",                             // Owner
              6: "trueOwner",                         // TrueOwner
              7: "manager"                            // Manager
            };

            
            // Default to first item in dropdown list
            const selectedResourceCode = propDefToResourceRefMap[propLocationId] || this._resourceOptions[0].code;

            let selectedResourceOptionValue = null;
            for (let resourceOption of this._resourceOptions) {
                if (resourceOption.code === selectedResourceCode) {
                    selectedResourceOptionValue = resourceOption.value;
                    break;
                }
            }

            if (selectedResourceOptionValue && editor.entryEditor && typeof editor.entryEditor.setValue === 'function') {
                editor.entryEditor.setValue(selectedResourceOptionValue);
            }
        }

        public doRender() {

            let formBuilderDiv = $("<div>").appendTo(this.container);
            let formBuilder = new Solovis.UI.InputControls.FormBuilder({
                container: formBuilderDiv,
                labelColumnWidth: 120,
                width: 440
            });
            this._formBuilder = formBuilder;

            let declarationTypeOptions = [
                { text: "Property Value", value: "PropertyValue" },
                { text: "Currency", value: "Currency" },
                { text: "Value Type", value: "ValueType" },
                { text: "Owner", value: "Owner" },
            ];

            if (Solovis.Portal.CachedModuleOptions.isEnabled("ExposureBasedComposites")) {
                declarationTypeOptions.push({ text: "Exposure", value: "Exposure" });
            }

            let valuesOverTime_Default = "Default";
            let valuesOverTime_FollowChangesOverTime = "FollowChangesOverTime";
            let valuesOverTime_MostRecentOnly = "MostRecentOnly";
            let valuesOverTimeOptions = [
                { text: "Top-Level Default", value: valuesOverTime_Default },
                { text: "Follow Changes Over Time", value: valuesOverTime_FollowChangesOverTime },
                { text: "Most Recent Only", value: valuesOverTime_MostRecentOnly },
            ];

            formBuilder.addEntry({
                name: "declarationType",
                label: "Type",

                initialValue: this._grouping ? this._grouping.declarationType : "PropertyValue",

                editor: {
                    type: "select",
                    selectOptions: {
                        data: declarationTypeOptions
                    }
                }
            });

            formBuilder.addEntry({
                name: "property",
                label: "Property",

                initialValue: this._grouping ? { id: this._grouping.propertyDefinitionId } : null,

                editor: {
                    type: "parameter",
                    parameterOptions: {
                      parameterType: "property",
                      settings: {
                        filter: { hideForCompositeGroupingActive: true },
                        showIncludeHiddenItemsRow: true
                      } 
                    }
                },

                isVisible: (formEntry) => {
                    let declarationType = formEntry.formBuilder.getValueByName("declarationType");
                    return declarationType === "PropertyValue";
                },

                postSetup: (formEntry) => {
                    const resourceRefEntry = this._formBuilder.getEntryByName("resourceRef");

                    const updateResourceRef = () => {
                        if (resourceRefEntry) {
                            this._updateResourceRefEditor(resourceRefEntry);
                        }
                    };
                    $(formEntry).on("valueChanged", () => {
                        updateResourceRef();
                    });
                }
            });

            formBuilder.addEntry({
                name: "resourceRef",
                label: "For",

                initialValue: (this._grouping && this._grouping.resourceRef) ? this._grouping.resourceRef : this._defaultResourceRefValue,

                editor: {
                    type: "select",
                    selectOptions: {
                        data: this._resourceOptions.map(option => ({ text: option.label, value: option.value }))
                    }
                },

                isVisible: (formEntry) => {
                    let declarationType = formEntry.formBuilder.getValueByName("declarationType");
                    return declarationType === "PropertyValue";
                }

            });

            let valuesOverTimeInitialValue = valuesOverTime_Default;
            if (this._grouping) {
                if (this._grouping.useOnlyMostRecentPropertyValue === true) {
                    valuesOverTimeInitialValue = valuesOverTime_MostRecentOnly;
                }
                else if (this._grouping.useOnlyMostRecentPropertyValue === false) {
                    valuesOverTimeInitialValue = valuesOverTime_FollowChangesOverTime;
                }
            }

            formBuilder.addEntry({
                name: "valuesOverTime",
                label: "Values Over Time",

                help: "'Top-Level Default' uses the top-level setting for 'Use Only Most Recent Property Value'. 'Most Recent Only' overrides that to use only the most recent property value for this property. "
                    + " 'Follow Changes Over Time' overrides top-level setting to always allow assets to move between groups as property values change over time.",

                initialValue: valuesOverTimeInitialValue,

                editor: {
                    type: "select",
                    selectOptions: {
                        data: valuesOverTimeOptions
                    }
                },

                isVisible: (formEntry) => {
                    let declarationType = formEntry.formBuilder.getValueByName("declarationType");
                    if (declarationType === "PropertyValue") {
                        let propDef = this._getSelectedPropDefIfPropGrouping();
                        if (propDef && !propDef.isStaticOverTime) {
                            return true;
                        }
                    }

                    return false;
                }
            });

            formBuilder.addEntry({
                name: "levelSplitter",
                label: "Level Splitter",

                help: {
                    html: "<p>The value of the string property will be divided at each 'Level Splitter' to create nested groups. Use the empty string (blank) for no splitting.</p>"
                        + "<p>Example: '||' would split 'Transportation||Ground||Autos' into three nested levels: 'Transportation', 'Ground', and 'Autos'.</p>"
                        + "<p>NOTE: Empty groups will always be removed, and whitespace will be trimmed from group names.</p>"
                },

                initialValue: (this._grouping && Solovis.isNotNull(this._grouping.levelSplitter)) ? this._grouping.levelSplitter : "/",

                editor: {
                    type: "text"
                },

                isVisible: (formEntry) => {
                    let propDef = this._getSelectedPropDefIfPropGrouping();                   
                    if (propDef) {
                        if (propDef.propertyTypeId === InvData.PropertyTypeIds.String) {
                            // Only show option for string properties.
                            return true;
                        }
                    }                        

                    return false;
                }
            });

            formBuilder.addEntry({
                name: "exposureProfile",
                label: "Exposure Profile",

                initialValue: this._grouping ? { id: this._grouping.exposureProfileId } : null,

                editor: {
                    type: "parameter",
                    parameterOptions: {
                        parameterType: "exposureProfile"
                    }
                },

                isVisible: (formEntry) => {
                    let declarationType = formEntry.formBuilder.getValueByName("declarationType");
                    return declarationType === "Exposure";
                }
            });

            formBuilder.addEntry({
                name: "exposureTagSet",
                label: "Exposure Tag Set",

                initialValue: this._grouping ? { tagSetId: this._grouping.exposureTagSetId } : null,

                editor: {
                    type: "parameter",
                    parameterOptions: {
                        parameterType: "tagSet"
                    }
                },

                isVisible: (formEntry) => {
                    let declarationType = formEntry.formBuilder.getValueByName("declarationType");
                    return declarationType === "Exposure";
                }
            });
            

            let groupByOwnerLevelOptions = [
                { text: "Owner", value: "Direct" },                
                { text: "True Owner", value: "True" },
                { text: "Absolute Owner", value: "Absolute" },
            ];
            let groupByOwnerLevelValue = "Direct";
            if (this._grouping) {
                if (this._grouping.groupByOwnerLevel) {
                    groupByOwnerLevelValue = this._grouping.groupByOwnerLevel;
                }
                else if (this._grouping.groupByTrueOwner) {
                    groupByOwnerLevelValue = "True";
                }
            }

            formBuilder.addEntry({
                name: "groupByOwnerLevel",
                label: "Owner Level",

                help: "By default, grouping will be based on direct owner unless 'True Owner' or 'Absolute Owner' is selected. 'Absolute Owner' means the true owner of top-most investment in a look-through chain.",

                initialValue: groupByOwnerLevelValue,

                editor: {
                    type: "select",
                    selectOptions: {
                        data: groupByOwnerLevelOptions
                    }
                },

                isVisible: (formEntry) => {
                    let declarationType = formEntry.formBuilder.getValueByName("declarationType");
                    return declarationType === "Owner";
                }
            });


            formBuilder.addEntry({
                name: "valueTypeTopLevelOnly",
                label: "Top Level Only",

                help: "When selected, it will only show Reported and Computed Estimate, not the underlying child groups of Reported.",

                initialValue: (this._grouping && this._grouping.valueTypeTopLevelOnly) ? this._grouping.valueTypeTopLevelOnly : false,

                editor: {
                    type: "bool"
                },

                isVisible: (formEntry) => {
                    let declarationType = formEntry.formBuilder.getValueByName("declarationType");
                    return declarationType === "ValueType";
                }
            });


            // TagRefGrouping options.
            let tagRefGrouping = (this._grouping && Solovis.isNotNull(this._grouping.tagRefGrouping)) ? this._grouping.tagRefGrouping : null;

            formBuilder.addEntry({
                name: "tagRefGrouping.maxLevel",
                label: "Max Level",

                help: {
                    html: "<p>If tag assigned to asset exceeds this level depth, it will be remapped to parent tag at this level.</p>"
                        + "<p>Root tags are at level 0.</p>"
                },

                initialValue: (tagRefGrouping && Solovis.isNotNull(tagRefGrouping.maxLevel)) ? tagRefGrouping.maxLevel : null,

                editor: {
                    type: "integer",                    
                },

                isVisible: (formEntry) => {
                    let propDef = this._getSelectedPropDefIfPropGrouping();
                    if (propDef) {
                        if (propDef.propertyTypeId === InvData.PropertyTypeIds.TagRef) {
                            // Only show option for tagref properties.
                            return true;
                        }
                    }

                    return false;
                }
            });

            formBuilder.addEntry({
                name: "tagRefGrouping.expansionFilter",
                label: "Expansion Filter",

                help: {
                    html: "<p>If tag is not included in tag set filter, it will be remapped to first parent tag that is included in the filter."
                        + " If no parent tag is in tag set filter, the asset will be treated as untagged and will appear in '(Other)' group if 'Create Other Groups' is enabled.</p>"
                },

                initialValue: (tagRefGrouping && Solovis.isNotNull(tagRefGrouping.expansionFilter)) ? tagRefGrouping.expansionFilter : null,

                createEditor: (createEditorOptions) => {

                    let editor: any = {};
                    editor.tagSetFilter = createEditorOptions.initialValue;

                    let editLink = $("<a>").text("Edit").appendTo(createEditorOptions.container);

                    let updateEditLinkText = () => {
                        if (editor.tagSetFilter) {
                            editLink.text("Edit");
                        }
                        else {
                            editLink.text("Create");
                        }
                    };
                    updateEditLinkText();

                    editLink.click(() => {
                        let propDef = this._getSelectedPropDefIfPropGrouping();
                        if (propDef) {
                            Solovis.Reports.TableFilters.TagFilterControl.showAsDialog(
                                {
                                    tagSetFilter: editor.tagSetFilter,
                                    tagSetId: propDef.tagRefTagSetId
                                },
                                (newFilter) => {
                                    editor.tagSetFilter = newFilter;
                                    updateEditLinkText();
                                });
                        }
                    });                    

                    return {
                        getValue: () => {
                            return editor.tagSetFilter;
                        },

                        setValue: (newValue) => {
                            editor.tagSetFilter = newValue;
                            updateEditLinkText();
                        }
                    };
                },                               

                isVisible: (formEntry) => {
                    let propDef = this._getSelectedPropDefIfPropGrouping();
                    if (propDef) {
                        if (propDef.propertyTypeId === InvData.PropertyTypeIds.TagRef) {
                            // Only show option for tagref properties.
                            return true;
                        }
                    }

                    return false;
                },

                applyStandardTextAlignTopPadding: true
            });

            let lastStateMonitor = {
                lastProperty: formBuilder.getValueByName("property")
            };

            formBuilder.bindValueChanged((e, data) => {
                // If selected property value is changed, we need to clear the values for tagRefGrouping.maxLevel/expansionFilter.
                if (data.formEntry.name === "property") {
                    let prop = data.formEntry.value();
                    let changed = false;
                    if (prop && !lastStateMonitor.lastProperty
                        || !prop && lastStateMonitor.lastProperty) {
                        changed = true;
                    }
                    else if (prop && lastStateMonitor.lastProperty) {
                        if (prop.id !== lastStateMonitor.lastProperty.id) {
                            changed = true;
                        }
                    }

                    lastStateMonitor.lastProperty = prop;

                    if (changed) {
                        formBuilder.getEntryByName("tagRefGrouping.maxLevel").value(null);
                        formBuilder.getEntryByName("tagRefGrouping.expansionFilter").value(null);
                        formBuilder.getEntryByName("valuesOverTime").value(valuesOverTime_Default);   
                    }
                }
            });

            formBuilder.setupDone();
        }

        public getGroupingDeclaration(): CompositeGroupingDeclarationData {
            let newGrouping: CompositeGroupingDeclarationData = {};

            let declarationType = this._formBuilder.getValueByName("declarationType");
            if (!declarationType) {
                return null;
            }

            newGrouping.declarationType = declarationType;

            if (declarationType === "PropertyValue") {
                let propDef = this._getSelectedPropDefIfPropGrouping();   

                let property = this._formBuilder.getValueByName("property");
                if (property) {
                    newGrouping.propertyDefinitionId = property.id;
                }

                let defaultResourceRefValue = "InvestmentHoldingOrEntity";
                let resourceRef = this._formBuilder.getValueByName("resourceRef");
                if (resourceRef !== defaultResourceRefValue) {
                    newGrouping.resourceRef = resourceRef;
                }

                let levelSplitter = this._formBuilder.getValueByName("levelSplitter");
                if (!levelSplitter) {
                    levelSplitter = '';
                }
                if (levelSplitter != '/') {
                    newGrouping.levelSplitter = levelSplitter;
                }

                let tagRefGroupingMaxLevel = this._formBuilder.getValueByName("tagRefGrouping.maxLevel");
                let tagRefGroupingExpansionFilter = this._formBuilder.getValueByName("tagRefGrouping.expansionFilter");
                if (Solovis.isNotNull(tagRefGroupingMaxLevel) || Solovis.isNotNull(tagRefGroupingExpansionFilter)) {
                    newGrouping.tagRefGrouping = {
                        maxLevel: tagRefGroupingMaxLevel,
                        expansionFilter: tagRefGroupingExpansionFilter
                    };
                }

                if (propDef && !propDef.isStaticOverTime) {
                    let valuesOverTime = this._formBuilder.getValueByName("valuesOverTime");
                    if (valuesOverTime === "FollowChangesOverTime") {
                        newGrouping.useOnlyMostRecentPropertyValue = false;
                    }
                    else if (valuesOverTime === "MostRecentOnly") {
                        newGrouping.useOnlyMostRecentPropertyValue = true;
                    }
                }
            }

            if (declarationType === "Exposure") {
                let exposureProfile = this._formBuilder.getValueByName("exposureProfile");
                if (exposureProfile) {
                    newGrouping.exposureProfileId = exposureProfile.id;

                    let exposureTagSet = this._formBuilder.getValueByName("exposureTagSet");
                    if (exposureTagSet) {
                        newGrouping.exposureTagSetId = exposureTagSet.tagSetId;
                    }
                }
            }

            if (declarationType == "Owner") {
                let groupByOwnerLevel = this._formBuilder.getValueByName("groupByOwnerLevel");
                if (groupByOwnerLevel) {
                    newGrouping.groupByOwnerLevel = groupByOwnerLevel;
                }
            }

            if (declarationType == "ValueType") {
                let topLevelOnly = this._formBuilder.getValueByName("valueTypeTopLevelOnly");
                if (topLevelOnly !== null) {
                    newGrouping.valueTypeTopLevelOnly = topLevelOnly;
                }
            }

            return newGrouping;
        }
    }
}